import React from "react";
import InfoIcon from "../InfoIcon";
import { typography, textColors, surfaces, borders } from "../../../design-system/tokens";

/* ─── Constants ────────────────────────────────────────────────────── */

const SPACING_VALUES = [
  "auto", "0", "1", "2", "3", "4", "5", "6", "8",
  "10", "12", "16", "20", "24", "32", "40", "48", "56", "64",
];

const PRESETS = [
  { label: "None", value: "0",  title: "Set all sides to 0 (none)" },
  { label: "1",    value: "1",  title: "Set all sides to 1" },
  { label: "2",    value: "2",  title: "Set all sides to 2" },
  { label: "4",    value: "4",  title: "Set all sides to 4" },
  { label: "8",    value: "8",  title: "Set all sides to 8" },
  { label: "16",   value: "16", title: "Set all sides to 16" },
];

/** Per-type visual theme — blue for margin, amber for padding. */
const THEMES = {
  margin: {
    outer:        "bg-blue-50/80 dark:bg-blue-950/40 border-blue-200/70 dark:border-blue-800/50",
    inner:        "bg-white dark:bg-dark-900 border-blue-200/50 dark:border-blue-700/40",
    badge:        "bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700",
    activePreset: "bg-blue-500 dark:bg-blue-600 text-white border-blue-500 dark:border-blue-600 shadow-sm",
    btnHover:     "hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:border-blue-300 dark:hover:border-blue-600",
  },
  padding: {
    outer:        "bg-amber-50/80 dark:bg-amber-950/40 border-amber-200/70 dark:border-amber-800/50",
    inner:        "bg-white dark:bg-dark-900 border-amber-200/50 dark:border-amber-700/40",
    badge:        "bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700",
    activePreset: "bg-amber-500 dark:bg-amber-600 text-white border-amber-500 dark:border-amber-600 shadow-sm",
    btnHover:     "hover:bg-amber-50 dark:hover:bg-amber-900/30 hover:border-amber-300 dark:hover:border-amber-600",
  },
};

const BOX_SIZE = 110;

/* ─── SVG Icons ────────────────────────────────────────────────────── */

const PlusIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <line x1="5" y1="2.5" x2="5" y2="7.5" />
    <line x1="2.5" y1="5" x2="7.5" y2="5" />
  </svg>
);

const MinusIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <line x1="2.5" y1="5" x2="7.5" y2="5" />
  </svg>
);

/* ─── Micro-components ─────────────────────────────────────────────── */

/** Small circular step button (+/−). */
const StepButton = ({ onClick, disabled, children, ariaLabel, theme }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    className={`
      w-[22px] h-[22px] flex items-center justify-center rounded-md border
      transition-all duration-150 select-none
      ${disabled
        ? `${surfaces.tertiary} ${borders.default} opacity-25 cursor-not-allowed`
        : `${surfaces.secondary} ${borders.default} ${textColors.secondary} ${theme.btnHover} active:scale-90`}
    `}
  >
    {children}
  </button>
);

/** Coloured badge showing the current spacing value for one side. */
const ValueBadge = ({ value, theme }) => (
  <span
    className={`
      inline-flex items-center justify-center min-w-[28px] h-[22px] px-1.5
      rounded-md border text-[11px] font-bold tabular-nums select-none
      ${theme.badge} transition-all duration-200
    `}
  >
    {value === "auto" ? "auto" : value}
  </span>
);

/** Horizontal [−] value [+] row — for top & bottom sides. */
const HorizontalControl = ({ value, onIncrease, onDecrease, canIncrease, canDecrease, theme, side }) => (
  <div className="flex items-center justify-center gap-1">
    <StepButton onClick={onDecrease} disabled={!canDecrease} ariaLabel={`Decrease ${side}`} theme={theme}>
      <MinusIcon />
    </StepButton>
    <ValueBadge value={value} theme={theme} />
    <StepButton onClick={onIncrease} disabled={!canIncrease} ariaLabel={`Increase ${side}`} theme={theme}>
      <PlusIcon />
    </StepButton>
  </div>
);

/** Vertical [+] value [−] column — for left & right sides. */
const VerticalControl = ({ value, onIncrease, onDecrease, canIncrease, canDecrease, theme, side }) => (
  <div className="flex flex-col items-center justify-center gap-0.5">
    <StepButton onClick={onIncrease} disabled={!canIncrease} ariaLabel={`Increase ${side}`} theme={theme}>
      <PlusIcon />
    </StepButton>
    <ValueBadge value={value} theme={theme} />
    <StepButton onClick={onDecrease} disabled={!canDecrease} ariaLabel={`Decrease ${side}`} theme={theme}>
      <MinusIcon />
    </StepButton>
  </div>
);

/** Gap between preset pills (`gap-1.5` → 0.375rem → 6px). */
const PRESET_GAP_PX = 6;

const presetButtonClass = (isActive, theme) => `
  px-2.5 py-0.5 rounded-lg text-[11px] font-semibold border
  transition-all duration-150 select-none whitespace-nowrap
  ${isActive
    ? theme.activePreset
    : `${surfaces.tertiary} ${textColors.secondary} ${borders.default} ${surfaces.hover}`}
`;

/**
 * Single-line preset pills that shed options from the end (16, then 8, …)
 * when the container is too narrow to fit them all.
 */
const PresetPills = ({ presets, theme, allSame, currentValue, onSetAll }) => {
  const containerRef = React.useRef(null);
  const labelRef = React.useRef(null);
  const ghostRefs = React.useRef([]);
  const [visibleCount, setVisibleCount] = React.useState(presets.length);

  React.useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const recompute = () => {
      const available = container.clientWidth;
      const labelW = labelRef.current ? labelRef.current.offsetWidth : 0;
      const widths = ghostRefs.current.map((el) => (el ? el.offsetWidth : 0));

      // Not yet measurable (hidden, zero-width, or pre-layout) → show everything
      // rather than collapsing the row to a single pill.
      if (!available || widths.some((w) => !w)) {
        setVisibleCount(presets.length);
        return;
      }

      // Largest prefix of presets that fits on one line alongside the label.
      let count = 1;
      for (let n = 1; n <= presets.length; n += 1) {
        let needed = labelW;
        for (let i = 0; i < n; i += 1) needed += PRESET_GAP_PX + widths[i];
        if (needed <= available) count = n;
        else break;
      }
      setVisibleCount(count);
    };

    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(container);
    return () => ro.disconnect();
  }, [presets.length]);

  const visiblePresets = presets.slice(0, visibleCount);

  return (
    <div
      ref={containerRef}
      className={`relative flex flex-nowrap items-center justify-center gap-1.5 mt-1 pt-2.5 border-t w-full overflow-hidden ${borders.light}`}
    >
      <span ref={labelRef} className={`${typography.caption} ${textColors.muted}`}>All:</span>
      {visiblePresets.map((preset) => {
        const isActive = allSame && currentValue === preset.value;
        return (
          <button
            key={preset.value}
            type="button"
            onClick={() => onSetAll(preset.value)}
            title={preset.title}
            className={presetButtonClass(isActive, theme)}
          >
            {preset.label}
          </button>
        );
      })}

      {/* Hidden measurement row — natural widths for the fit calculation. */}
      <div
        aria-hidden="true"
        className="absolute left-0 top-0 flex flex-nowrap items-center gap-1.5 invisible pointer-events-none"
      >
        {presets.map((preset, i) => (
          <button
            key={preset.value}
            type="button"
            tabIndex={-1}
            ref={(el) => { ghostRefs.current[i] = el; }}
            className={presetButtonClass(false, theme)}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  );
};

/* ─── Main Component ───────────────────────────────────────────────── */

export const SpacingField = ({ value, onChange, label, help, field, infoText, labelSize = "normal" }) => {
  const formattedLabel =
    typeof label === "string" && label.length > 0
      ? label.charAt(0).toUpperCase() + label.slice(1)
      : label;

  const isMargin = field === "margin";
  const theme = isMargin ? THEMES.margin : THEMES.padding;

  /* ── Helpers ──────────────────────────────────────────────────────── */

  const getCurrentIndex = (spacingValue) => {
    const idx = SPACING_VALUES.indexOf(spacingValue);
    return idx >= 0 ? idx : 0;
  };

  /** Map a spacing token to a pixel inset inside the visualisation box. */
  const getVisualOffset = (spacingValue) => {
    if (spacingValue === "auto" || spacingValue === "0") return 4;
    const num = parseInt(spacingValue, 10);
    if (isNaN(num) || num <= 0) return 4;
    return 4 + Math.min(32, (num / 64) * 32);
  };

  const handleIncrease = (side) => {
    const cur = value?.[side] || "0";
    const idx = getCurrentIndex(cur);
    if (idx < SPACING_VALUES.length - 1) {
      onChange({ id: value?.id, ...value, [side]: SPACING_VALUES[idx + 1] });
    }
  };

  const handleDecrease = (side) => {
    const cur = value?.[side] || "0";
    const idx = getCurrentIndex(cur);
    if (idx > 0) {
      onChange({ id: value?.id, ...value, [side]: SPACING_VALUES[idx - 1] });
    }
  };

  const handleSetAll = (v) => {
    onChange({ id: value?.id, top: v, right: v, bottom: v, left: v });
  };

  /* ── Derived state ──────────────────────────────────────────────── */

  const currentValues = {
    top:    value?.top    || "0",
    right:  value?.right  || "0",
    bottom: value?.bottom || "0",
    left:   value?.left   || "0",
  };

  const offsets = {
    top:    getVisualOffset(currentValues.top),
    right:  getVisualOffset(currentValues.right),
    bottom: getVisualOffset(currentValues.bottom),
    left:   getVisualOffset(currentValues.left),
  };

  const allSame =
    currentValues.top === currentValues.right &&
    currentValues.right === currentValues.bottom &&
    currentValues.bottom === currentValues.left;

  const canIncrease = (side) => getCurrentIndex(currentValues[side]) < SPACING_VALUES.length - 1;
  const canDecrease = (side) => getCurrentIndex(currentValues[side]) > 0;

  const labelClass =
    labelSize === "small"
      ? `${typography.bodySmall} font-medium ${textColors.secondary} mb-2`
      : `block mb-2 ${typography.label} ${textColors.secondary}`;

  /* ── Render ─────────────────────────────────────────────────────── */

  return (
    <div className={labelSize === "small" ? "space-y-2" : "field-wrapper"}>
      {/* Label */}
      {label && (
        <label className={labelClass}>
          {formattedLabel}
          {infoText && labelSize === "normal" ? (
            <span className="ml-2 align-middle"><InfoIcon text={infoText} /></span>
          ) : null}
        </label>
      )}

      <div className="flex flex-col items-center gap-1.5">
        {/* ── Top ─────────────────────────────────── */}
        <HorizontalControl
          side="top"
          value={currentValues.top}
          onIncrease={() => handleIncrease("top")}
          onDecrease={() => handleDecrease("top")}
          canIncrease={canIncrease("top")}
          canDecrease={canDecrease("top")}
          theme={theme}
        />

        {/* ── Left · Box · Right ──────────────────── */}
        <div className="flex items-center gap-1.5">
          <VerticalControl
            side="left"
            value={currentValues.left}
            onIncrease={() => handleIncrease("left")}
            onDecrease={() => handleDecrease("left")}
            canIncrease={canIncrease("left")}
            canDecrease={canDecrease("left")}
            theme={theme}
          />

          {/* Visual box model */}
          <div
            className={`relative rounded-xl border-2 ${theme.outer} shadow-sm overflow-hidden`}
            style={{ width: BOX_SIZE, height: BOX_SIZE }}
          >
            {/* Inner "content" region */}
            <div
              className={`absolute rounded-lg border ${theme.inner} transition-all duration-300 ease-out`}
              style={{
                top:    offsets.top,
                right:  offsets.right,
                bottom: offsets.bottom,
                left:   offsets.left,
                minWidth:  10,
                minHeight: 10,
              }}
            />
          </div>

          <VerticalControl
            side="right"
            value={currentValues.right}
            onIncrease={() => handleIncrease("right")}
            onDecrease={() => handleDecrease("right")}
            canIncrease={canIncrease("right")}
            canDecrease={canDecrease("right")}
            theme={theme}
          />
        </div>

        {/* ── Bottom ──────────────────────────────── */}
        <HorizontalControl
          side="bottom"
          value={currentValues.bottom}
          onIncrease={() => handleIncrease("bottom")}
          onDecrease={() => handleDecrease("bottom")}
          canIncrease={canIncrease("bottom")}
          canDecrease={canDecrease("bottom")}
          theme={theme}
        />

        {/* ── Preset pills ────────────────────────── */}
        <PresetPills
          presets={PRESETS}
          theme={theme}
          allSame={allSame}
          currentValue={currentValues.top}
          onSetAll={handleSetAll}
        />
      </div>

      {/* Help */}
      {help && (
        <div className={`${typography.caption} ${textColors.tertiary} mt-2`}>{help}</div>
      )}
    </div>
  );
};
